import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

/** A single redirect rule served by the public site. */
interface Redirect {
	id: string;
	fromPath: string;
	toPath: string;
	isPermanent: boolean;
	source: string;
	hits: number;
}

/** Shape of the GET /api/redirects response. */
interface RedirectListResponse {
	items: Redirect[];
	total: number;
}

/** Pull a readable message out of an axios/unknown error. */
const extractErrorMessage = (error: unknown): string | null => {
	if (error && typeof error === "object" && "response" in error && (error as any).response) {
		const response = (error as any).response;
		const message = response?.data?.message ?? response?.data ?? response?.statusText ?? null;
		if (typeof message === "string") {
			const truncated = message.split(" at APA.ADMIN")[0]?.trim();
			return truncated?.length ? truncated : message;
		}
		return null;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return null;
};

/** Manage the redirect rules that the public site serves for legacy URLs. */
const Redirections: React.FC = () => {
	const [items, setItems] = useState<Redirect[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);

	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");

	const [fromPath, setFromPath] = useState("");
	const [toPath, setToPath] = useState("");
	const [isPermanent, setIsPermanent] = useState(true);
	const [submitting, setSubmitting] = useState(false);

	const [publishing, setPublishing] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);

	// Debounce the search field so typing doesn't fire a request per keystroke.
	useEffect(() => {
		const handle = window.setTimeout(() => {
			setSearch(searchInput.trim());
		}, 300);
		return () => window.clearTimeout(handle);
	}, [searchInput]);

	const loadRedirects = useCallback(async () => {
		setLoading(true);
		setLoadError(null);
		try {
			const response = await axios.get<RedirectListResponse>("/api/redirects", {
				params: { search: search || undefined, page: 1, limit: 100 }
			});
			setItems(response.data?.items ?? []);
			setTotal(response.data?.total ?? 0);
		} catch (error) {
			console.error("Failed to load redirects", error);
			setItems([]);
			setTotal(0);
			setLoadError(extractErrorMessage(error) ?? "Unable to load redirects.");
		} finally {
			setLoading(false);
		}
	}, [search]);

	useEffect(() => {
		loadRedirects();
	}, [loadRedirects]);

	const handleAddRedirect = async (event: React.FormEvent) => {
		event.preventDefault();
		const from = fromPath.trim();
		const to = toPath.trim();
		if (!from || !to) {
			toast.error("Both the From and To paths are required.");
			return;
		}
		setSubmitting(true);
		try {
			await axios.post("/api/redirects", { fromPath: from, toPath: to, isPermanent });
			toast.success("Redirect added.");
			setFromPath("");
			setToPath("");
			setIsPermanent(true);
			await loadRedirects();
		} catch (error) {
			console.error("Failed to add redirect", error);
			toast.error(extractErrorMessage(error) ?? "Unable to add the redirect.");
		} finally {
			setSubmitting(false);
		}
	};

	const handleDelete = async (id: string) => {
		setDeletingId(id);
		try {
			await axios.delete("/api/redirects/" + id);
			toast.success("Redirect deleted.");
			await loadRedirects();
		} catch (error) {
			console.error("Failed to delete redirect", error);
			toast.error(extractErrorMessage(error) ?? "Unable to delete the redirect.");
		} finally {
			setDeletingId(null);
		}
	};

	const handlePublish = async () => {
		setPublishing(true);
		try {
			await axios.post("/api/redirects/publish");
			toast.success("redirects.json published");
		} catch (error) {
			console.error("Failed to publish redirects", error);
			toast.error(extractErrorMessage(error) ?? "Unable to publish redirects.");
		} finally {
			setPublishing(false);
		}
	};

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-8">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Redirections</p>
						<h1 className="text-2xl font-semibold text-slate-900">Manage redirects</h1>
						<p className="text-sm text-slate-500">
							Create and audit redirects to keep legacy URLs healthy and SEO-friendly.
						</p>
					</div>
					<button
						type="button"
						onClick={handlePublish}
						disabled={publishing}
						title="Writes redirects.json — the file the public site reads to serve these rules."
						className="ml-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{publishing ? "Publishing…" : "Publish redirects"}
					</button>
				</div>

				<section className="space-y-6">
					<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Add a redirect</p>
						<p className="mt-1 text-sm text-slate-500">
							Define a new rule. Remember to publish afterwards so the public site picks it up.
						</p>
						<form onSubmit={handleAddRedirect} className="mt-4 flex flex-wrap items-end gap-4">
							<label className="flex flex-1 flex-col gap-1 sm:min-w-[220px]">
								<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">From path</span>
								<input
									type="text"
									value={fromPath}
									onChange={(e) => setFromPath(e.target.value)}
									placeholder="/old-url"
									className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-400 focus:bg-white focus:outline-none"
								/>
							</label>
							<label className="flex flex-1 flex-col gap-1 sm:min-w-[220px]">
								<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">To path</span>
								<input
									type="text"
									value={toPath}
									onChange={(e) => setToPath(e.target.value)}
									placeholder="/new-url"
									className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-400 focus:bg-white focus:outline-none"
								/>
							</label>
							<label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
								<input
									type="checkbox"
									checked={isPermanent}
									onChange={(e) => setIsPermanent(e.target.checked)}
									className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
								/>
								Permanent (301)
							</label>
							<button
								type="submit"
								disabled={submitting}
								className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
							>
								{submitting ? "Adding…" : "Add redirect"}
							</button>
						</form>
					</div>

					<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
						<div className="mb-4 flex flex-wrap items-center gap-3">
							<div>
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Redirects</p>
								<p className="mt-1 text-sm text-slate-500">
									{total} {total === 1 ? "rule" : "rules"}
									{search ? ` matching "${search}"` : ""}
								</p>
							</div>
							<div className="ml-auto flex flex-1 items-center sm:max-w-sm">
								<input
									type="search"
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									placeholder="Search redirects…"
									className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-400 focus:bg-white focus:outline-none"
								/>
							</div>
						</div>

						{loadError && (
							<p className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
								{loadError}
							</p>
						)}

						{loading && !items.length ? (
							<p className="text-sm text-slate-500">Loading redirects…</p>
						) : items.length ? (
							<div className="overflow-x-auto">
								{loading && <p className="mb-2 text-xs text-slate-500">Refreshing…</p>}
								<table className="min-w-full divide-y divide-slate-200 text-sm">
									<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
										<tr>
											<th scope="col" className="px-4 py-3 text-left">
												From → To
											</th>
											<th scope="col" className="px-4 py-3 text-left">
												Type
											</th>
											<th scope="col" className="px-4 py-3 text-left">
												Source
											</th>
											<th scope="col" className="px-4 py-3 text-left">
												Hits
											</th>
											<th scope="col" className="px-4 py-3 text-left">
												Action
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-slate-100">
										{items.map((redirect) => (
											<tr key={redirect.id} className="align-top">
												<td className="px-4 py-3">
													<p className="font-semibold text-slate-900">{redirect.fromPath}</p>
													<p className="text-xs text-slate-500">→ {redirect.toPath}</p>
												</td>
												<td className="px-4 py-3">
													<span
														className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
															redirect.isPermanent
																? "bg-emerald-100 text-emerald-700"
																: "bg-amber-100 text-amber-700"
														}`}
													>
														{redirect.isPermanent ? "301" : "302"}
													</span>
												</td>
												<td className="px-4 py-3 text-xs text-slate-600">{redirect.source || "—"}</td>
												<td className="px-4 py-3 text-slate-700">{redirect.hits}</td>
												<td className="px-4 py-3">
													<button
														type="button"
														onClick={() => handleDelete(redirect.id)}
														disabled={deletingId === redirect.id}
														className="rounded-full border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
													>
														{deletingId === redirect.id ? "Deleting…" : "Delete"}
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<p className="text-sm text-slate-500">
								{search ? "No redirects match your search." : "No redirects defined yet."}
							</p>
						)}
					</div>
				</section>
			</div>
		</div>
	);
};

export default Redirections;
